import {Error, ReferenceError, documentElement, toggleClass} from '@acng/frontend-bounty';

import {
  STYLE_ACTIVE,
  STYLE_HIDDEN,
  STYLE_INACTIVE,
  STYLE_PLAYING,
  STYLE_RECORDING,
  STYLE_WAITING,
} from 'acng/layout/config/style';

import {ctxLivecamSession} from 'acng/livecam/context/session.js';
import {inject} from 'acng/core/service/ng.js';
import {spinner} from 'acng/core/service/spinner.js';

import {sendMessage} from '../service/send-message.js';
import {sendVoice} from '../service/send-voice.js';
import {uploadVoiceMessage} from '../service/sonus.js';
import {CTX_VALUE} from '@acng/frontend-relativity/minify';

/**
 * @typedef {import("../model/message").MessageData} MessageData
 * @typedef {import("acng/amateurPool/factory/Amateur").Amateur} Amateur
 * @typedef {import('../model/message').AttachmentData} AttachmentData
 */

/**
 * @implements {MessageData}
 * @todo TODO should extend Message
 */
export class MessageComposer {
  body = '';
  time = NaN;
  isMine = true;
  payload = /** @type {MessageData["payload"]} */ ({});
  attachment = /** @type {MessageData["attachment"]} */ (null);

  allowVoice = inject('user').hasVoiceMessage() && !ctxLivecamSession[CTX_VALUE](documentElement);
  /**
   * @type {Blob | boolean}
   */
  voice = false;
  playing = false;
  recording = false;
  wait = false;

  /**
   * @param {Amateur | null} amateur
   */
  constructor(amateur) {
    this.sender = `${inject('user').id}`;
    this.recipient = `${amateur?.id ?? NaN}`;
    this.amateur = amateur;
    this.allowVoice = this.allowVoice && !!amateur?.hasVoiceMessages();
  }

  /**
   * @param {HTMLElement} context
   */
  async send(context) {
    if (!this.amateur) {
      throw ReferenceError();
    }

    if (this.wait) {
      throw Error();
    }

    this.wait = true;

    /**
     * @type {AttachmentData | null}
     */
    let nextAttachment = null;
    let nextBody = '';

    try {
      if (this.voice instanceof Blob) {
        const sonusRes = await spinner(uploadVoiceMessage(context, this.voice), context);

        nextAttachment = this.attachment;
        nextBody = this.body;
        this.attachment = null;
        this.body = '';
        this.payload = {type: 'voice', id: sonusRes.id};

        await sendVoice(context, this.amateur, this);
        this.voice = false;
      } else {
        await sendMessage(context, this.amateur, this);
      }
      await inject('sock').publish('message.sent', {...this, amateur: undefined, voiceBlob: undefined});
    } catch (reason) {
      DEBUG: console.error(reason);
    }

    this.attachment = nextAttachment;
    this.body = nextBody;
    this.payload = {};

    this.wait = false;
  }

  /**
   * @param {Element} element
   */
  applyState(element) {
    toggleClass(element, STYLE_HIDDEN, !this.allowVoice);
    toggleClass(element, STYLE_INACTIVE, !this.voice || (this.voice === true && this.wait));
    toggleClass(element, STYLE_RECORDING, this.voice === true && !this.wait);
    toggleClass(element, STYLE_WAITING, this.wait);
    toggleClass(element, STYLE_ACTIVE, this.voice instanceof Blob);
    toggleClass(element, STYLE_PLAYING, this.playing);
  }
}
